import * as BABYLON from "babylonjs/babylon.max";
import cabri from "../app/cabri-app";

const StateBits = {
    // generated from DEcore.h line 72 and ff
    DE_OBJECT_DEFINED_BIT:     0x000001, // Object is defined
    DE_OBJECT_ACTIVE_BIT:      0x000002, // Object is active
    DE_OBJECT_SELECTED_BIT:    0x000004, // Object is selected
    DE_OBJECT_HIGHLIGHTED_BIT: 0x000008, // Object is highlighted
    DE_OBJECT_GRAYED_BIT:      0x000010, // Object is grayed
    DE_OBJECT_CONSTRUCTION_INPUT_BIT: 0x000020, // Object is one of current
                                                // construction inputs
    DE_OBJECT_LOCKED_BIT:      0x000040, // Object is locked (eg. can't manipulated)
    DE_OBJECT_BUMPED_BIT:      0x000080, // Object is bumbed (ex: when created)
    DE_OBJECT_NODISPLAY_BIT:   0x000100, // Object can't be displayed
    DE_OBJECT_NOSERIALIZE_BIT: 0x000200, // Object can't be serialized
    DE_OBJECT_NOPICKING_BIT:   0x000400, // Object can't be picked
    DE_OBJECT_INACTIVABLE_BIT: 0x000800, // Object may be inactivated
    DE_OBJECT_NOOPERATE_BIT:   0x001000, // Object can't be operated
    DE_OBJECT_MACRO_IN_BIT:    0x010000, // Object is a macro input (inside a
                                         // 'macro')
    DE_OBJECT_MACRO_OUT_BIT:   0x020000, // Object is a macro output (inside a 'macro')
    DE_OBJECT_MACRO_OPT_BIT:   0x040000, // Object is an optional macro input
    DE_OBJECT_LOCOPERATE_BIT:  0x080000, // Object operate in locus mode
    DE_OBJECT_NOUNDO_BIT:      0x100000, // Object can't be serialized for undo/redo
    DE_ALL_OBJECT_STATE_BITS:  0x1F1FFF, // All state bits
    DE_SERIALIZED_OBJECT_STATE_BITS: 0x070740, // All serialized state bits
};

class GeomObject {
// update scene algo
    /*
     - reset all updatedInLastcycle, toUpdateInLastCycle
     - go through list which pulls updateFromPeer
     - pull info from DE
     - if moved or changed
     - redefines or moves
     - sets updatedInLastCycle and alreadyChecked to true
     - updateFromPeer returns this so dependent ones can actualize
     - if called again (alreadyChecked = true), then just return updatedInLastCycle

     */

    constructor (objectId) {
        this.updatedInLastCycle = false;
        this.alreadyCheckedInLastCycle = false;
        this.noPicking = false;
        this.objectId = objectId || 0;
        this.readId();
        if (this.objectId !== 0) {
            this.noDisplay = 0 !== cabri.emsc.getStateBits(this.objectId,
                StateBits.DE_OBJECT_NODISPLAY_BIT);
            this.noPicking = 0 !== cabri.emsc.getStateBits(this.objectId,
                StateBits.DE_OBJECT_NOPICKING_BIT);
            cabri.allObjects.push(this);
        }
    }

    clearForCycleStart () {
        this.updatedInLastCycle = false;
        this.alreadyCheckedInLastCycle = false;
    }

    dispose () {
        if (this.objectId)
            cabri.emsc.disposeOne(this.objectId);
        let index = cabri.allObjects.indexOf(this);
        if (index !== -1)
            cabri.allObjects.splice(index, 1);
        if (this.idString)
            delete cabri.objects[this.idString];
    }

    setId (id) {
        this.idString = id;
    }

    getId () {
        return this.idString;
    }

    setLayers (l) {
        if (l) {
            this.layers = l.split(" ");
            this.hidden =
                this.layers.length == 1 && this.layers[0] === "hidden_layer";
        }
    }

/** Generic method to assign the material from the elementary color information.
 * @param bjsobj Babylon object on which to assign object.
 * @param color a string such as a hex color or a cabri color name
 * @param options object of options to add to the material (e.g.
 *     <code>{alpha: 0.7}</code>)
 * */
    markColor (bjsobj, color, options) {
        if (!color) {
            color = this.color;
        }
        if (!color) {
            color = "gray";
        }
        if (typeof (color) == "string") {
            if (cabri && cabri.colors && cabri.colors[color])
                this.color = cabri.colors[color];
            bjsobj.material = new BABYLON.StandardMaterial("..", cabri.scene);

// defaults are: a black(0,0,0), D white(1,1,1), e black, (0,0,0), S white(1,1,1)
            bjsobj.material.diffuseColor = BABYLON.Color3.FromHexString(color);
            bjsobj.material.emissiveColor =
                BABYLON.Color3.FromHexString("#000000");
            bjsobj.material.specularColor =
                BABYLON.Color3.FromHexString("#000000");

            this.material = bjsobj.material;
        }
        else  if (typeof (this.material) === "object")
                bjsobj.material = this.material;

        // assign each assigned property of options to the material
// JML it is dangerous ta assign properties to material object without control.
// material is a semi private property of meshes
// better to avoid
        if (options)
            Object.getOwnPropertyNames(options).forEach(n =>
                bjsobj.material[n] = options[n] )    // todo to check that each property from options is actually a material property
    } // ends markColor()

    readId () {
        if (this.objectId === 0) {
            this.setId("test-object");
            return;
        }
        let t = cabri.emsc.getId(this.objectId);
        this.setId("" + t);
    }

    getStyleAttribute (name, defaultValue) {
        return this.readAtt(name, defaultValue, "style");
    }

    getFloatStyleAttribute (name, defaultValue) {
        let v = this.readAtt(name, defaultValue, "style");
        let w = Number.parseFloat(v);
        let r;
        if (Number.isNaN(w)) {
            r = defaultValue;
        }
        else {
            r = w;
        }
        //console.log("[readAtt] Converted floatStyle to " + r);
        return r;
    }

    getColorAttribute (name, defaultValue) {
        return this.readAtt(name, defaultValue, "color");
    }

    isConstrained() {
        if(this.objectId === 0)
            return false;
        const relClass = cabri.emsc.getControllingRelationClass(this.objectId);
        return relClass !== null && relClass.indexOf('_on_') > -1;
    }

    readAtt (name, defaultValue, styleOrColor) {
        //console.log("[readAtt] Reading att " + name + " of " + this);
        let att = "" + cabri.emsc.getAttribute(this.objectId, name, 0);
        //console.log("[readAtt] Obtained att \"" + att + "\".");
        if (typeof (att) === "undefined" || att === "" || att === "0" || att === 0)
            att = defaultValue;
        if (styleOrColor === "style") {
            if (typeof att === "string" && cabri.styles[att])
                att = cabri.styles[att];
        }
        else
            if (styleOrColor === "color") {
                if (typeof att === "string" && cabri.colors[att]) {
                    att = cabri.colors[att];
                }
            }
            else {
                // nothing done... we don't interpret the value
            }
        // console.log("[readAtt] Resolved att " + att);
        return att;
    }

    highlightThisPeer (peer, color) {
        peer.material.emissiveColor = color;
    }

    registerHighlighter (bjsobj, highlightColor, deHighlightColor, extraListener) {
        if (this.noPicking) {
            return;
        }
        this.highlightColor   = highlightColor || new BABYLON.Color3(0.3, 0.1, 0.3);
        this.deHighlightColor = deHighlightColor || new BABYLON.Color3(0, 0, 0);
        bjsobj.actionManager = new BABYLON.ActionManager(cabri.scene);
        let highlightOnAction = new BABYLON.ExecuteCodeAction(
            BABYLON.ActionManager.OnPointerOverTrigger,
            (evt) => {
                if (true || cabri.highlightingActive)    // JML experiment 2021/11/26
                    bjsobj.material.emissiveColor = this.highlightColor
                else {
                    bjsobj.material.emissiveColor = BABYLON.Color3.FromHexString("#000000");
                    bjsobj.material.diffuseColor = BABYLON.Color3.FromHexString("#000000");
                    bjsobj.material.ambientColor = BABYLON.Color3.FromHexString("#000000");
                }
                if(extraListener) extraListener(true);
            }),
            highlightOffAction = new BABYLON.ExecuteCodeAction(
                BABYLON.ActionManager.OnPointerOutTrigger,
                (evt) => {
                    if (cabri.highlightingActive)
                        bjsobj.material.emissiveColor = this.deHighlightColor
                    if (false)                          // JML experiment 2021/11/26
                        bjsobj.material.emissiveColor = BABYLON.Color3.FromHexString("#00FF00");
                    if(extraListener) extraListener(false);
                });
        bjsobj.actionManager.registerAction(highlightOnAction);
        bjsobj.actionManager.registerAction(highlightOffAction);

        let clickObj = this;
        let clickAction = new BABYLON.ExecuteCodeAction(
            BABYLON.ActionManager.OnPickTrigger, (evt) => {
                console.log("Clicked " + clickObj.idString +
                    "( available as window.clicked)", clickObj);
                window.clicked = clickObj;
            },
        );
        bjsobj.actionManager.registerAction(clickAction);

    }

    /*
     Empty function.
     */
    updateFromPeer () {

    }

    /**
     * Indicate if a relation can be created with the other objects.
     * This method returns false but overwriting may give other values.
     * @param other the GeomObject object to be related to.
     */
    canBeRelatedWith (other) {
        return false;
    }

    /**
     * Create the {@link GeomRelation} if {@link #canBeRelatedWith()} returns true.
     * @param other the GeomObject object to be related to.
     */
    createRelationWith ( other ) {
        return null;
    }



}

export {StateBits, GeomObject};